// UploadModal.jsx - Clean, professional upload modal
import React, { useState } from 'react';
import './UploadModal.css';
import avatarImg from '../images/Jesse_avatar.webp';

const UploadModal = ({ isVisible, onClose, onFileUploaded }) => {
  const [dragActive, setDragActive] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [showConfirmDialog, setShowConfirmDialog] = useState(false);
  const [pendingFile, setPendingFile] = useState(null);
  const [alertMessage, setAlertMessage] = useState('');
  const [alertType, setAlertType] = useState('warning');
  const [showAlert, setShowAlert] = useState(false);

  const handleFiles = async (files) => {
    const file = files[0];
    if (!file) return;

    const allowedTypes = ['.txt', '.docx', '.doc', '.pdf'];
    const fileName = file.name.toLowerCase();
    const hasValidExtension = allowedTypes.some(ext => fileName.endsWith(ext));

    if (!hasValidExtension) {
      showAlertMessage('Momenteel ondersteunen we .txt, .docx, .doc en .pdf bestanden.', 'warning');
      return;
    }

    const maxSize = fileName.endsWith('.pdf') ? 10 * 1024 * 1024 : 5 * 1024 * 1024;
    if (file.size > maxSize) {
      const maxSizeMB = fileName.endsWith('.pdf') ? '10MB' : '5MB';
      showAlertMessage(`Bestand is te groot (max ${maxSizeMB})`, 'warning');
      return;
    }

    setUploading(true);

    try {
      const text = await readFileContent(file);

      if (!isLikelyVacancyText(text)) {
        setPendingFile({ file, text });
        setShowConfirmDialog(true);
        setUploading(false);
        return;
      }

      onFileUploaded(text, file.name);
      onClose();
    } catch (error) {
      console.error('File reading error:', error);
      showAlertMessage('Fout bij het lezen van het bestand. Probeer een ander formaat of kopieer de tekst naar een .txt bestand.', 'error');
    } finally {
      setUploading(false);
    }
  };

  const showAlertMessage = (message, type = 'warning') => {
    setAlertMessage(message);
    setAlertType(type);
    setShowAlert(true);
  };

  const handleConfirmProceed = () => {
    if (pendingFile) {
      onFileUploaded(pendingFile.text, pendingFile.file.name);
      onClose();
    }
    setShowConfirmDialog(false);
    setPendingFile(null);
  };

  const handleConfirmCancel = () => {
    setShowConfirmDialog(false);
    setPendingFile(null);
  };

  const readFileContent = async (file) => {
    const fileName = file.name.toLowerCase();

    return new Promise(async (resolve, reject) => {
      try {
        if (fileName.endsWith('.txt')) {
          const reader = new FileReader();
          reader.onload = (e) => {
            const text = e.target.result;
            const cleanText = cleanTextContent(text);
            if (cleanText.length < 50) {
              reject(new Error('Bestand bevat te weinig leesbare tekst'));
              return;
            }
            resolve(cleanText);
          };
          reader.onerror = () => reject(new Error('Kon bestand niet lezen'));
          reader.readAsText(file, 'UTF-8');

        } else if (fileName.endsWith('.docx') || fileName.endsWith('.doc')) {
          try {
            const arrayBuffer = await file.arrayBuffer();
            const mammoth = await import('mammoth');
            const result = await mammoth.extractRawText({ arrayBuffer });

            const cleanText = cleanTextContent(result.value);
            if (cleanText.length < 50) {
              reject(new Error('Kon geen tekst vinden in het Word document'));
              return;
            }
            resolve(cleanText);
          } catch (error) {
            console.error('Word document error:', error);
            reject(new Error('Kon Word document niet lezen. Probeer het bestand op te slaan als .txt'));
          }

        } else if (fileName.endsWith('.pdf')) {
          try {
            const arrayBuffer = await file.arrayBuffer();
            const text = await extractTextFromPDF(arrayBuffer);
            const cleanText = cleanTextContent(text);

            if (cleanText.length < 50) {
              reject(new Error('Kon geen tekst vinden in het PDF bestand. Probeer een tekst-gebaseerde PDF.'));
              return;
            }
            resolve(cleanText);
          } catch (error) {
            console.error('PDF error:', error);
            reject(new Error('Kon PDF niet lezen. Kopieer de tekst handmatig naar een .txt bestand.'));
          }
        } else {
          reject(new Error('Onbekend bestandstype'));
        }
      } catch (error) {
        reject(error);
      }
    });
  };

  const extractTextFromPDF = async (arrayBuffer) => {
    const uint8Array = new Uint8Array(arrayBuffer);
    const text = new TextDecoder('utf-8').decode(uint8Array);

    const textMatches = text.match(/\(([^)]+)\)/g);
    if (textMatches) {
      return textMatches
        .map(match => match.slice(1, -1))
        .join(' ')
        .replace(/\\[rn]/g, '\n');
    }

    const readableText = text.replace(/[^\x20-\x7E\n\r]/g, ' ').trim();
    if (readableText.length > 100) {
      return readableText;
    }

    throw new Error('Geen leesbare tekst gevonden in PDF');
  };

  const cleanTextContent = (text) => {
    if (!text) return '';
    return text
      .replace(/\r\n/g, '\n')
      .replace(/\r/g, '\n')
      .replace(/\t/g, ' ')
      .replace(/\f/g, '\n')
      .replace(/\v/g, '\n')
      .replace(/\u00A0/g, ' ')
      .replace(/\u2000-\u200F/g, ' ')
      .replace(/  +/g, ' ')
      .replace(/\n\s*\n\s*\n/g, '\n\n')
      .trim();
  };

  const isLikelyVacancyText = (text) => {
    const lowerText = text.toLowerCase();
    const vacancyKeywords = [
      'functie', 'vacature', 'werkzaamheden', 'taken', 'vereisten',
      'sollicitant', 'kandidaat', 'baan', 'werk', 'verantwoordelijkheden',
      'opleiding', 'ervaring', 'salaris', 'arbeidsvoorwaarden',
      'wij zoeken', 'je gaat', 'je doet', 'wij bieden', 'functie-eisen',
      'solliciteren', 'bedrijf', 'team', 'collega'
    ];
    const keywordCount = vacancyKeywords.filter(keyword =>
      lowerText.includes(keyword)
    ).length;
    return keywordCount >= 2 && text.length >= 100;
  };

  if (!isVisible) return null;

  return (
    <>
      <div className="upload-modal-overlay" onClick={onClose}>
        <div className="upload-modal-content" onClick={e => e.stopPropagation()}>

          {/* Header — brand gradient + avatar (matches VacancyReviewScreen) */}
          <div className="upload-header">
            <img className="upload-header__avatar" src={avatarImg} alt="Jesse" />
            <div className="upload-header__text">
              <h2>Vacaturetekst uploaden</h2>
              <p>Upload je bestaande tekst en laat Jesse hem verbeteren</p>
            </div>
            <button className="upload-close-btn" onClick={onClose} aria-label="Sluiten">
              <span className="material-icons">close</span>
            </button>
          </div>

          <div className="upload-modal-body">
            {/* Drop zone */}
            <div
              className={`upload-zone ${dragActive ? 'drag-active' : ''} ${uploading ? 'uploading' : ''}`}
              onDragEnter={(e) => { e.preventDefault(); setDragActive(true); }}
              onDragLeave={(e) => { e.preventDefault(); setDragActive(false); }}
              onDragOver={(e) => e.preventDefault()}
              onDrop={(e) => {
                e.preventDefault();
                setDragActive(false);
                if (!uploading) handleFiles(e.dataTransfer.files);
              }}
            >
              {uploading ? (
                <div className="upload-loading">
                  <div className="loading-spinner"></div>
                  <p className="upload-loading__title">Bestand wordt gelezen...</p>
                  <p className="upload-loading__sub">Even geduld, we verwerken je vacaturetekst</p>
                </div>
              ) : (
                <>
                  <div className="upload-zone__icon">
                    <span className="material-icons">
                      {dragActive ? 'downloading' : 'cloud_upload'}
                    </span>
                  </div>
                  <p className="upload-zone__title">
                    {dragActive ? 'Laat los om te uploaden' : 'Sleep je bestand hierheen'}
                  </p>
                  <p className="upload-zone__sub">of kies een bestand van je computer</p>
                  <label htmlFor="upload-input" className="upload-button">
                    <span className="material-icons">upload_file</span>
                    Bestand kiezen
                  </label>
                  <div className="supported-formats">
                    <span className="format-tag">PDF</span>
                    <span className="format-tag">DOCX</span>
                    <span className="format-tag">DOC</span>
                    <span className="format-tag">TXT</span>
                  </div>

                  <input
                    type="file"
                    accept=".txt,.docx,.doc,.pdf"
                    onChange={(e) => handleFiles(e.target.files)}
                    style={{ display: 'none' }}
                    id="upload-input"
                    disabled={uploading}
                  />
                </>
              )}
            </div>

            {/* What Jesse does — compact info block */}
            <div className="upload-info">
              <h4 className="upload-info__title">
                <span className="material-icons">auto_fix_high</span>
                Wat doet Jesse met je tekst?
              </h4>
              <div className="upload-info__items">
                <span>Pakkender intro schrijven</span>
                <span className="upload-info__dot" aria-hidden="true"></span>
                <span>Eisen realistischer maken</span>
                <span className="upload-info__dot" aria-hidden="true"></span>
                <span>Voordelen concreter formuleren</span>
              </div>
              <p className="upload-info__note">
                Upload ook imperfecte concepten — na upload kun je verder verfijnen via de chat
              </p>
            </div>
          </div>
        </div>
      </div>

      {/* Confirm Dialog */}
      {showConfirmDialog && (
        <div className="confirm-overlay">
          <div className="confirm-dialog">
            <div className="modal-icon warning">
              <span className="material-icons">help_outline</span>
            </div>
            <div className="confirm-header" style={{ background: 'none', padding: '16px 24px 0' }}>
              <h3 style={{ color: '#2d3748', textAlign: 'center' }}>Waarschuwing</h3>
            </div>
            <div className="confirm-body">
              <p>Dit lijkt geen vacaturetekst te zijn. Wil je toch doorgaan?</p>
              <p className="confirm-subtitle">
                Jesse werkt het best met teksten die functieomschrijvingen, werkzaamheden of vereisten bevatten.
              </p>
            </div>
            <div className="confirm-actions">
              <button className="confirm-btn secondary" onClick={handleConfirmCancel}>
                Annuleren
              </button>
              <button className="confirm-btn primary" onClick={handleConfirmProceed}>
                Toch doorgaan
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Alert Dialog */}
      {showAlert && (
        <div className="confirm-overlay">
          <div className="confirm-dialog">
            <div className={`modal-icon ${alertType}`}>
              <span className="material-icons">
                {alertType === 'error' ? 'error' : alertType === 'warning' ? 'warning' : alertType === 'success' ? 'check_circle' : 'info'}
              </span>
            </div>
            <div className="confirm-body" style={{ textAlign: 'center' }}>
              <h3 style={{ marginTop: '16px', marginBottom: '12px', color: '#2d3748' }}>
                {alertType === 'error' ? 'Fout' : alertType === 'warning' ? 'Melding' : alertType === 'success' ? 'Succes' : 'Informatie'}
              </h3>
              {alertMessage.split('\n').map((line, index) => (
                <p key={index}>{line}</p>
              ))}
            </div>
            <div className="confirm-actions">
              <button
                className="confirm-btn primary"
                onClick={() => setShowAlert(false)}
              >
                Begrepen
              </button>
            </div>
          </div>
        </div>
      )}
    </>
  );
};

export default UploadModal;
